import { useState } from 'react';
import { Modal, ModalBody, type PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { AiCreditsOrdwayPurchaseForm } from './ai-credits-ordway-purchase-form';
import { AI_CREDITS_CONFIG } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { CreditsPurchaseForm } from './credits-purchase-form';

export type AiCreditsPurchaseModalData = {
  /** Ordway accounts use the (card-on-file) Ordway form; everyone else uses the Stripe form. */
  useOrdway: boolean;
  /** The surface that opened the modal, reported with every event the form fires. */
  pageName: CreditsPurchasePageName;
};

export type AiCreditsPurchaseResult = { savedCard: boolean };

/**
 * Managed modal for purchasing AI credits. Hosts the Ordway or Stripe purchase form per `useOrdway`,
 * confirming with the purchase result so the opener can refresh balances. While a purchase is
 * processing the modal is non-dismissable.
 */
export function AiCreditsPurchaseModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<AiCreditsPurchaseModalData, AiCreditsPurchaseResult>) {
  const [isProcessing, setIsProcessing] = useState(false);

  return (
    <Modal
      data-test-ai-credits-purchase-modal
      state={modalState}
      title="Purchase AI credits"
      isDismissable={!isProcessing}
    >
      <ModalBody>
        {data.useOrdway ? (
          <AiCreditsOrdwayPurchaseForm
            pageName={data.pageName}
            onSuccess={onConfirm}
            onCancel={onClose}
            onProcessingChange={setIsProcessing}
          />
        ) : (
          <CreditsPurchaseForm
            config={AI_CREDITS_CONFIG}
            pageName={data.pageName}
            onSuccess={onConfirm}
            onCancel={onClose}
            onProcessingChange={setIsProcessing}
          />
        )}
      </ModalBody>
    </Modal>
  );
}
